// hooks/useMultiFieldFormHandler.ts
import { useState } from "react";

type Errors<T> = Partial<Record<keyof T, string>> & { backend?: string };

type UseMultiFieldFormHandler<T> = {
  validate: (values: T) => Errors<T> | null;
  onSubmit: (values: T) => void;
  useDialog?: boolean;
};

export default function useMultiFieldFormHandler<T>(
  initialValues: T,
  options: UseMultiFieldFormHandler<T>
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Errors<T>>({});
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = <K extends keyof T>(key: K, value: T[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = options.validate(values);
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }
    if (options.useDialog) {
      setDialogOpen(true);
    } else {
      options.onSubmit(values);
    }
  };

  const handleProceed = () => {
    setDialogOpen(false);
    options.onSubmit(values);
  };

  return { values, errors, dialogOpen, setDialogOpen, handleChange, handleSubmit, handleProceed, setErrors };
}
